import { z } from 'zod';
import { GameRuntime } from '../game-runtime.js';

/**
 * Game-dev job model (Cycle-2 SG-4, D-035/D-040/D-062). A "job" is a request to
 * act on a game — generate it, iterate it, or fix it — that a game-dev RUNNER
 * (Claude Code / an LLM agent in an ephemeral env) executes asynchronously. The
 * API only ever *persists + enqueues* a job; it never calls a runner. This file
 * is the loosely-coupled contract (Step 1a — types only, no infra, no runner).
 *
 * Storage (when the handler lands, Step 1b): job items live on the EXISTING
 * games table as part of the per-game item collection (D-034):
 *   pk = gameId, sk = `job#{jobId}`
 * so one Query still loads a game with its versions, shares, AND jobs.
 *
 * Gated by `FEATURES.gameDevBackend` (off until a runner exists).
 */

export const GameJobKind = z.enum(['generate', 'iterate', 'fix']);
export type GameJobKind = z.infer<typeof GameJobKind>;

export const GameJobStatus = z.enum([
  'queued',
  'running',
  'succeeded',
  'failed',
  'cancelled',
]);
export type GameJobStatus = z.infer<typeof GameJobStatus>;

/** Which runner implementation handled (or will handle) a job. */
export const RunnerKind = z.enum(['local-dev', 'ec2-bootstrap', 'fargate', 'in-house']);
export type RunnerKind = z.infer<typeof RunnerKind>;

/** Job item (sk='job#{jobId}'). */
export const GameJobSchema = z.object({
  jobId: z.string().min(1),
  gameId: z.string().min(1),
  kind: GameJobKind,
  /** The user's instruction for this job (e.g. "make the player jump higher"). */
  prompt: z.string().min(1).max(2000),
  status: GameJobStatus.default('queued'),
  /** For iterate/fix: the version node the job starts from (branch action,
   *  VT-002). Lets the UI anchor a pending-branch "ghost node" via listJobs. */
  parentVersionId: z.string().optional(),
  /** Branch label for the produced version — job-result copies it onto the
   *  VersionNode (the job item is the durable carrier; the event contract and
   *  runner stay untouched, VT-002 / plan §4 R-4). */
  branchLabel: z.string().max(120).optional(),
  /** Set when a runner claims the job. */
  runnerKind: RunnerKind.optional(),
  /** Version node the job produced on success (links into the version tree). */
  resultVersionId: z.string().optional(),
  /** Failure detail when status='failed'. */
  error: z.string().max(2000).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type GameJob = z.infer<typeof GameJobSchema>;

/** What the client POSTs to enqueue a job. Server assigns the rest. */
export const CreateJobInput = z.object({
  kind: GameJobKind,
  prompt: z.string().min(1).max(2000),
});
export type CreateJobInput = z.infer<typeof CreateJobInput>;

/**
 * The pluggable runner contract (D-035/D-040). Interchangeable implementations —
 * `local-dev` (a sandboxed local process on the operator's dev machine — the
 * current bootstrap path, replacing the off-limits cloud-dev EC2 box; see D-085),
 * `ec2-bootstrap` (superseded — the original cloud-dev box path), `fargate`
 * (ideal-state spin-to-zero), `in-house` (future) — all satisfy the SAME
 * contract: long-poll the job queue, execute against an ephemeral worktree, and
 * emit a `game.job.completed` | `game.job.failed` event on the domain bus. No
 * implementation here — Step 1a defines the interface only; the live consumer is
 * a later, gated step (the API depends on this type, never on a concrete runner).
 */
export interface GameDevRunner {
  /** Which implementation this is. */
  readonly kind: RunnerKind;
  /**
   * Claim and execute one job, then emit a `game.job.*` result event. The API
   * never calls this synchronously — runners consume the queue out-of-band.
   * (Signature intentionally minimal at the contract level; concrete runners
   * own their queue-poll / worktree / push details.)
   */
  run(job: GameJob): Promise<void>;
}

// ─── SQS job-message contract (API enqueue → runner) ────────────────────────
// The body the API enqueues to the game-dev-jobs queue (game-jobs.ts) and the
// runner consumes (poller.ts). One schema so producer + consumer can't drift.
// `parentVersionId`/`parentArtifactKey` are optional — the API only populates
// them for iterate/fix jobs (a later enqueue enhancement); the runner reads them
// when present.

export const GameJobMessageSchema = z.object({
  jobId: z.string().min(1),
  gameId: z.string().min(1),
  kind: GameJobKind,
  prompt: z.string().min(1).max(2000),
  parentVersionId: z.string().optional(),
  parentArtifactKey: z.string().optional(),
  /** Backend runtime the runner builds for (D-089). Defaults to `static-html`
   *  so pre-existing producers/messages (no field) stay valid; the enqueue side
   *  populates `multiplayer-server` only once the gate + server path land. */
  runtime: GameRuntime.default('static-html'),
  /** Quickstart master the game was created from (AS-007) — resolved at
   *  ENQUEUE from the game's templateAnswers (quickstartTemplateIdFromAnswers)
   *  because the runner is DB-free (D-085). Stamped UNCONDITIONALLY (it's
   *  data, not behavior); absent for 5-step-flow games + pre-existing
   *  messages/synthetic seeders. Consumed by the runner only when the
   *  asset-packs gate is active (FEATURES.assetPacks / RUNNER_ASSET_PACKS). */
  quickstartTemplateId: z.string().optional(),
});
export type GameJobMessage = z.infer<typeof GameJobMessageSchema>;

// ─── game-artifact S3 key convention (Cycle-3 Step 2b) ───────────────────────
// The single source for where a game's produced files live in the
// `game-artifacts` bucket. The runner writes these keys; a future play surface
// reads them. Keeping the convention in one place prevents producer/consumer drift.

export const GAME_ARTIFACT_FILE = 'game.html';

/**
 * A `multiplayer-server` artifact is multiple SOURCE files (D-091..D-093) — the
 * ws host (Fargate, cycle 6) builds + runs them, mirroring the zombie-multiplayer
 * `deploy.sh` (docker build at deploy), so the runner uploads source, not a bundle.
 * These are the files the runner writes (under the version prefix via
 * `gameArtifactKey`) and the host reads. One list so producer + host can't drift.
 */
export const MULTIPLAYER_ARTIFACT_FILES = [
  'package.json',
  'tsconfig.json',
  'src/game.ts',
  'src/index.ts',
  'web/index.html',
] as const;
export type MultiplayerArtifactFile = (typeof MULTIPLAYER_ARTIFACT_FILES)[number];

/** S3 key for a game version's artifact, e.g. `games/{gameId}/{versionId}/game.html`. */
export function gameArtifactKey(
  gameId: string,
  versionId: string,
  file: string = GAME_ARTIFACT_FILE,
): string {
  return `games/${gameId}/${versionId}/${file}`;
}

// ─── runner → result-Lambda event contract (Cycle-3 Step 2b, D-073) ──────────
// The runner emits these on the DomainBus; the `job-result` Lambda consumes them.
// EventBridge envelope: { Source: GAME_JOB_EVENT_SOURCE, DetailType, Detail }.

export const GAME_JOB_EVENT_SOURCE = 'dgm.gamedev';
export const GameJobDetailType = {
  completed: 'game.job.completed',
  failed: 'game.job.failed',
} as const;

/** `game.job.completed` detail — a runner finished a job and produced a version. */
export const GameJobCompletedDetail = z.object({
  jobId: z.string().min(1),
  gameId: z.string().min(1),
  /** The new version the job produced (becomes a `ver#` node). */
  versionId: z.string().min(1),
  changeLabel: z.string().min(1).max(120),
  parentVersionId: z.string().optional(),
  /** S3 key of the produced game artifact (games/{gameId}/{versionId}/game.html). */
  artifactKey: z.string().optional(),
  thumbnailKey: z.string().optional(),
  runnerKind: RunnerKind.optional(),
});
export type GameJobCompletedDetail = z.infer<typeof GameJobCompletedDetail>;

/** `game.job.failed` detail — a runner could not complete a job. */
export const GameJobFailedDetail = z.object({
  jobId: z.string().min(1),
  gameId: z.string().min(1),
  error: z.string().max(2000),
  runnerKind: RunnerKind.optional(),
});
export type GameJobFailedDetail = z.infer<typeof GameJobFailedDetail>;
